import toast from 'react-hot-toast';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import styles from './UserProfileModal.module.css';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { Loader2, Plus, Check, Ban, Grid, List, Search, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProfileModal = ({ userId, onClose, onBlockChange }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [addingShows, setAddingShows] = useState({});
  const [myShows, setMyShows] = useState([]);
  const [blocking, setBlocking] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [searchQuery, setSearchQuery] = useState('');
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);

  useEffect(() => {
    loadUserProfile();
    loadMyShows();
  }, []);

  const loadMyShows = async () => {
    try {
      const response = await axios.get(`${API}/shows`, { withCredentials: true });
      setMyShows(response.data);
    } catch (error) {
      console.error('Error loading my shows:', error);
    }
  };

  const loadUserProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/users/${userId}/profile`,
        { withCredentials: true }
      );
      console.log('User Profile Response:', response.data);
      console.log('Stats data:', response.data.stats);
      setProfile(response.data);
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUser = async () => {
    console.log('Block button clicked for user:', userId);
    setShowBlockConfirm(false); // Close dialog
    
    console.log('Calling POST /api/blocks/' + userId);
    setBlocking(true);
    try {
      const response = await axios.post(`${API}/blocks/${userId}`, {}, { withCredentials: true });
      console.log('Block response:', response.data);
      toast.success('User has been blocked');
      
      // Notify parent to refresh lists
      if (onBlockChange) {
        onBlockChange();
      }
      
      onClose(); // Close modal after blocking
    } catch (error) {
      console.error('Error blocking user:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to block user');
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblockUser = async () => {
    console.log('Unblock button clicked for user:', userId);
    setShowUnblockConfirm(false); // Close dialog
    
    console.log('Calling DELETE /api/blocks/' + userId);
    setBlocking(true);
    try {
      const response = await axios.delete(`${API}/blocks/${userId}`, { withCredentials: true });
      console.log('Unblock response:', response.data);
      toast.success('User has been unblocked');
      
      // Notify parent to refresh lists
      if (onBlockChange) {
        onBlockChange();
      }
      
      // Reload profile to update button
      await loadUserProfile();
    } catch (error) {
      console.error('Error unblocking user:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to unblock user');
    } finally {
      setBlocking(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getYear = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.getFullYear();
  };

  const isShowInMyList = (showTitle) => {
    return myShows.some(show => show.title === showTitle);
  };

  const addShowToMyList = async (review) => {
    setAddingShows({ ...addingShows, [review.id]: 'adding' });
    
    try {
      // Add show to user's Want to Watch list
      const response = await axios.post(
        `${API}/shows`,
        {
          title: review.show_title,
          image_url: review.show_image_url || null,
          status: 'Want to Watch',
          streaming_service: null,
          custom_service: null,
          personal_rating: null,
          personal_review: null
        },
        { withCredentials: true }
      );

      // Update myShows list to include the new show
      setMyShows([...myShows, response.data]);

      setAddingShows({ ...addingShows, [review.id]: 'added' });
      setTimeout(() => {
        setAddingShows({ ...addingShows, [review.id]: null });
      }, 2000);
    } catch (error) {
      console.error('Error adding show:', error);
      setAddingShows({ ...addingShows, [review.id]: 'error' });
      setTimeout(() => {
        setAddingShows({ ...addingShows, [review.id]: null });
      }, 2000);
    }
  };

  return (
    <>
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className={styles.userProfileModal} data-testid="user-profile-modal">
        <DialogHeader className={styles.modalHeader}>
          <DialogTitle>User Profile</DialogTitle>
        </DialogHeader>

        <div className={styles.modalContent}>
          {loading ? (
            <div className={styles.loadingState}>
              <Loader2 className="spinner" />
              <p>Loading profile...</p>
            </div>
          ) : profile ? (
            <>
              <div className={styles.profileHeader}>
                <Avatar className={styles.profileAvatar}>
                  <AvatarImage src={profile.user.picture} alt={profile.user.name} />
                  <AvatarFallback>{profile.user.name[0]}</AvatarFallback>
                </Avatar>
                <div className={styles.profileInfo}>
                  <h2>{profile.user.name}</h2>
                  <p className={styles.username}>@{profile.user.username}</p>
                </div>
              </div>

              <div className={styles.profileActions}>
                {profile.is_blocked ? (
                  <Button
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Unblock button clicked - event triggered');
                      setShowUnblockConfirm(true);
                    }}
                    disabled={blocking}
                    className="unblock-btn"
                    data-testid="unblock-user-btn"
                  >
                    {blocking ? (
                      <><Loader2 className={styles.spinnerSm} /> Unblocking...</>
                    ) : (
                      <>Unblock User</>
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Block button clicked - event triggered');
                      setShowBlockConfirm(true);
                    }}
                    disabled={blocking}
                    className={styles.blockBtn}
                    data-testid="block-user-btn"
                  >
                    {blocking ? (
                      <><Loader2 className={styles.spinnerSm} /> Blocking...</>
                    ) : (
                      <><Ban size={16} /> Block User</>
                    )}
                  </Button>
                )}
              </div>

              {!profile.is_following && (
                <div className={styles.followNotice}>
                  <p>Follow this user to see their reviews</p>
                </div>
              )}

              {profile.stats && (
                <div className={styles.statsSection}>
                  <h3>BaadTV Stats</h3>
                  <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                      <div className={styles.statNumber}>
                        {profile.stats.average_rating !== null && profile.stats.average_rating !== undefined 
                          ? profile.stats.average_rating 
                          : 'N/A'}
                      </div>
                      <div className={styles.statLabel}>Avg Rating</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statNumber}>
                        {profile.stats.highest_rating !== null && profile.stats.highest_rating !== undefined 
                          ? profile.stats.highest_rating 
                          : 'N/A'}
                      </div>
                      <div className={styles.statLabel}>Highest Rating</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statNumber}>
                        {profile.stats.lowest_rating !== null && profile.stats.lowest_rating !== undefined 
                          ? profile.stats.lowest_rating 
                          : 'N/A'}
                      </div>
                      <div className={styles.statLabel}>Lowest Rating</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={styles.statNumber}>
                        {profile.stats.total_reviews !== undefined ? profile.stats.total_reviews : 0}
                      </div>
                      <div className={styles.statLabel}>Total Reviews</div>
                    </div>
                  </div>
                </div>
              )}

              {profile.is_following && (
                <div className={styles.reviewsSection}>
                  <div className={styles.reviewsHeader}>
                    <h3>Shared Reviews</h3>
                    <div className={styles.reviewsControls}>
                      <div className={styles.searchContainer}>
                        <Search size={16} className={styles.searchIcon} />
                        <input
                          type="text"
                          placeholder="Search reviews..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className={styles.searchInput}
                        />
                        {searchQuery && (
                          <button
                            className={styles.clearSearch}
                            onClick={() => setSearchQuery('')}
                            title="Clear search"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      <div className={styles.viewToggle}>
                        <button
                          className={`${styles.viewBtn} ${viewMode === 'list' ? styles.active : ''}`}
                          onClick={() => setViewMode('list')}
                          title="List view"
                        >
                          <List size={18} />
                        </button>
                        <button
                          className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.active : ''}`}
                          onClick={() => setViewMode('grid')}
                          title="Grid view"
                        >
                          <Grid size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                  {profile.reviews.length === 0 ? (
                    <div className={styles.noReviews}>
                      <p>No reviews shared yet</p>
                    </div>
                  ) : (() => {
                    const filteredReviews = profile.reviews.filter(review => {
                      if (!searchQuery.trim()) return true;
                      const query = searchQuery.toLowerCase();
                      return review.show_title.toLowerCase().includes(query) ||
                             review.review_text?.toLowerCase().includes(query);
                    });

                    if (filteredReviews.length === 0) {
                      return (
                        <div className={styles.noReviews}>
                          <p>No reviews found matching "{searchQuery}"</p>
                        </div>
                      );
                    }

                    return (
                      <>
                        {/* Desktop Reviews */}
                        <div className={`${styles.reviewsList} ${styles.desktopReviews} ${viewMode === 'grid' ? styles.gridView : styles.listView}`}>
                          {filteredReviews.map((review) => (
                            <div key={review.id} className={styles.reviewCard} data-testid={`review-${review.id}`}>
                              <div className={styles.reviewContent}>
                                {viewMode === 'grid' ? (
                                  // Grid view: Use backdrop (landscape) image
                                  review.backdrop_url ? (
                                    <img src={review.backdrop_url} alt={review.show_title} className={styles.reviewPoster} />
                                  ) : review.show_image_url && (
                                    <img src={review.show_image_url} alt={review.show_title} className={styles.reviewPoster} />
                                  )
                                ) : (
                                  // List view: Use poster (portrait) image
                                  review.show_image_url && (
                                    <img src={review.show_image_url} alt={review.show_title} className={styles.reviewPoster} />
                                  )
                                )}
                                <div className={styles.reviewDetails}>
                                  <div className={styles.reviewHeader}>
                                    <h4>{review.show_title}</h4>
                                    <div className={styles.reviewRating}>⭐ {review.rating}/10</div>
                                  </div>
                                  {(review.release_date || review.genres?.length > 0) && (
                                    <div className={styles.reviewMeta}>
                                      {review.release_date && <span className={styles.reviewYear}>{getYear(review.release_date)}</span>}
                                      {review.genres?.length > 0 && (
                                        <span className={styles.reviewGenres}>{review.genres.slice(0, 2).join(', ')}</span>
                                      )}
                                    </div>
                                  )}
                                  <p className={styles.reviewText}>{review.review_text}</p>
                                  <div className={styles.reviewDate}>{formatDate(review.created_at)}</div>
                                </div>
                              </div>
                              {!isShowInMyList(review.show_title) && (
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addShowToMyList(review);
                                  }}
                                  disabled={addingShows[review.id] === 'adding'}
                                  className={styles.addToListBtn}
                                  size="sm"
                                >
                                  {addingShows[review.id] === 'adding' ? (
                                    <>
                                      <Loader2 className={`${styles.btnIcon} ${styles.spinning}`} />
                                      Adding...
                                    </>
                                  ) : addingShows[review.id] === 'added' ? (
                                    <>
                                      <Check className={styles.btnIcon} />
                                      Added!
                                    </>
                                  ) : (
                                    <>
                                      <Plus className={styles.btnIcon} />
                                      Add to Want to Watch
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Mobile Reviews */}
                        <div className={`${styles.reviewsList} ${styles.mobileReviews} ${viewMode === 'grid' ? styles.mobileGridView : styles.mobileListView}`}>
                          {filteredReviews.map((review) => (
                        <div key={review.id} className={styles.reviewCard} data-testid={`review-${review.id}`}>
                          <div className={styles.reviewContent}>
                            {viewMode === 'grid' ? (
                              // Grid view: Use backdrop (landscape) image
                              review.backdrop_url ? (
                                <img src={review.backdrop_url} alt={review.show_title} className={styles.reviewPoster} />
                              ) : review.show_image_url && (
                                <img src={review.show_image_url} alt={review.show_title} className={styles.reviewPoster} />
                              )
                            ) : (
                              // List view: Use poster (portrait) image
                              review.show_image_url && (
                                <img src={review.show_image_url} alt={review.show_title} className={styles.reviewPoster} />
                              )
                            )}
                            <div className={styles.reviewDetails}>
                              <div className={styles.reviewHeader}>
                                <h4>{review.show_title}</h4>
                                <div className={styles.reviewRating}>⭐ {review.rating}/10</div>
                              </div>
                              {(review.release_date || review.genres?.length > 0) && (
                                <div className={styles.reviewMeta}>
                                  {review.release_date && <span className={styles.reviewYear}>{getYear(review.release_date)}</span>}
                                  {review.genres?.length > 0 && (
                                    <span className={styles.reviewGenres}>{review.genres.slice(0, 2).join(', ')}</span>
                                  )}
                                </div>
                              )}
                              <p className={styles.reviewText}>{review.review_text}</p>
                              <div className={styles.reviewDate}>{formatDate(review.created_at)}</div>
                            </div>
                          </div>
                          {!isShowInMyList(review.show_title) && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                addShowToMyList(review);
                              }}
                              disabled={addingShows[review.id] === 'adding'}
                              className={styles.addToListBtn}
                              size="sm"
                            >
                              {addingShows[review.id] === 'adding' ? (
                                <>
                                  <Loader2 className={`${styles.btnIcon} ${styles.spinning}`} />
                                  Adding...
                                </>
                              ) : addingShows[review.id] === 'added' ? (
                                <>
                                  <Check className={styles.btnIcon} />
                                  Added!
                                </>
                              ) : (
                                <>
                                  <Plus className={styles.btnIcon} />
                                  Add to Want to Watch
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </>
          ) : (
            <div className="error-state">
              <p>Failed to load profile</p>
            </div>
          )}
        </div>

      </DialogContent>
    </Dialog>

    {/* Block Confirmation Dialog */}
    <AlertDialog open={showBlockConfirm} onOpenChange={setShowBlockConfirm}>
      <AlertDialogContent className="alert-dialog-high-z">
        <AlertDialogHeader>
          <AlertDialogTitle>Block User</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to block this user? They will not be able to follow you and you will not see their content.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleBlockUser} className="bg-red-600 hover:bg-red-700">
            Block User
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Unblock Confirmation Dialog */}
    <AlertDialog open={showUnblockConfirm} onOpenChange={setShowUnblockConfirm}>
      <AlertDialogContent className="alert-dialog-high-z">
        <AlertDialogHeader>
          <AlertDialogTitle>Unblock User</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to unblock this user? They will be able to follow you and you will see their content again.
            <br /><br />
            <strong>Note:</strong> They will not appear in your flock unless you add them.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleUnblockUser}>
            Unblock User
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <style jsx global>{`
      .alert-dialog-high-z {
        z-index: 10002 !important;
      }
      [data-radix-alert-dialog-overlay] {
        z-index: 10001 !important;
      }
    `}</style>
    </>
  );
};

export default UserProfileModal;
